import { ReactNode, useEffect } from 'react';
import { Alert, Box, Spinner } from '@cloudscape-design/components';
import { Authenticator } from '@aws-amplify/ui-react';
import { signInWithRedirect } from 'aws-amplify/auth';

import { useAuth } from '../hooks/use-auth';

interface AuthGuardProps {
  children: ReactNode;
}

const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN;

/**
 * Route guard component that protects child routes from unauthenticated access.
 *
 * - If VITE_COGNITO_DOMAIN is set (SAML configured), redirects to Cognito hosted UI.
 * - If not set, shows the Amplify Authenticator form (email/password only).
 * - If the token has expired, shows "Your session has expired. Please sign in again."
 * - While loading, shows a centered spinner.
 *
 * Requirements: 1.1, 1.3, 1.7
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const { isLoading, isAuthenticated, error } = useAuth();

  // Show loading state while checking session
  if (isLoading) {
    return (
      <Box textAlign="center" padding={{ top: 'xxxl' }}>
        <Spinner size="large" />
      </Box>
    );
  }

  // Session expired or error — show message and re-authenticate
  if (error || !isAuthenticated) {
    const isExpiry = error?.toLowerCase().includes('expired') ||
      error?.toLowerCase().includes('token');

    if (isExpiry) {
      return (
        <Box textAlign="center" padding={{ top: 'xxxl' }}>
          <Alert type="warning" header="Session expired">
            Your session has expired. Please sign in again.
          </Alert>
        </Box>
      );
    }

    // If SAML/OAuth is configured, redirect to Cognito hosted UI
    if (cognitoDomain) {
      return <HostedUIRedirect />;
    }

    // No SAML — use Amplify Authenticator (email/password form)
    return (
      <Authenticator variation="modal" loginMechanisms={['email']}>
        {() => <>{children}</>}
      </Authenticator>
    );
  }

  return <>{children}</>;
}

/**
 * Component that triggers a redirect to the Cognito hosted UI.
 * Shows a spinner while the redirect is in progress.
 */
function HostedUIRedirect() {
  useEffect(() => {
    signInWithRedirect();
  }, []);

  return (
    <Box textAlign="center" padding={{ top: 'xxxl' }}>
      <Spinner size="large" />
      <Box variant="p" padding={{ top: 's' }}>Redirecting to sign in...</Box>
    </Box>
  );
}
